/**
 * Vehicle types come from the CONFIGURATIONS/OPTIONS/VehicleType config, which the
 * store already loads as `data.vehicletyperef` — the same source the Vue optimizer
 * reads. Shape: `{ label, value: { id, Name, Weight, Volume, ... } }`.
 */
export interface VehicleTypeRefEntry { label?: string; value?: any }

/** First of `values` that reads as a number, else undefined. */
const firstNumeric = (...values: any[]): number | undefined => {
  for (const v of values) {
    if (v === null || v === undefined || v === '') continue
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return undefined
}

const typeName = (t: VehicleTypeRefEntry): string => t?.value?.Name || t?.label || ''

/**
 * Selectable type names. Vehicle type is a required field, so the list must include
 * types no vehicle carries yet; `fallbackTypes` (the types actually in use) covers
 * the window before the config loads.
 */
export const vehicleTypeNames = (
  vehicleTypeRef: VehicleTypeRefEntry[] | undefined,
  fallbackTypes: string[],
): string[] => {
  const configured = (vehicleTypeRef ?? []).map(typeName).filter(Boolean)
  return configured.length > 0 ? Array.from(new Set(configured)) : fallbackTypes
}

/**
 * A type's default capacities, in the METRIC dataSourceUnit the config stores them
 * in (a 53' Trailer's Weight of 19958.048 is kg). Field naming has drifted across
 * projects, hence the fallbacks — the same set Vue accepts. A field is undefined
 * when the type declares no default for it.
 */
export const vehicleTypeDefaults = (
  vehicleTypeRef: VehicleTypeRefEntry[] | undefined,
  name: string,
): { weightCapacity?: number; volumeCapacity?: number } | undefined => {
  if (!name) return undefined
  const cfg = (vehicleTypeRef ?? []).find((t) => typeName(t) === name)?.value
  if (!cfg) return undefined
  return {
    weightCapacity: firstNumeric(cfg.WeightCapacity, cfg.weightCapacity, cfg.Weight, cfg.weight),
    volumeCapacity: firstNumeric(cfg.VolumeCapacity, cfg.volumeCapacity, cfg.Volume, cfg.volume),
  }
}
